import { TextInput } from 'lego-on-react';
import React, { useReducer, useState } from 'react';

import block from 'propmods';

import { requestMerchantAccess } from '../../../api';
import { MerchantInfo } from '../../../model/billing';
import { Skill } from '../../../model/skill';
import { merchantAccessTooltipReducer } from '../../../reducers/billing';
import { Field, Header } from '../../Form/Form';
import TextArea from '../../Form/TextArea';
import TooltipButton from '../../TooltipButton/TooltipButton';
import { requestCaption, requestDescriptionCaption, requestTokenCaption } from '../descriptions';

import './MerchantRequestForm.scss';

const b = block('MerchantRequestForm');

interface Props {
    skill: Skill;
    afterRequest: (merchantInfo: MerchantInfo) => void;
    merchants: MerchantInfo[];
    disabled: boolean;
}

const maxDescriptionLength = 150;

const validateToken = (token: string) => {
    if (!token) {
        return 'Это обязательное поле';
    }

    if (!token.match(/^[a-z0-9\-]*$/i)) {
        return 'Токен содержит недопустимые символы';
    }

    return '';
};

const MerchantRequestForm: React.FC<Props> = ({ skill, afterRequest, merchants, disabled }) => {
    const [token, setToken] = useState('');
    const [description, setDescription] = useState('');
    const [tooltipState, dispatchTooltipAction] = useReducer(merchantAccessTooltipReducer, { tooltipVisible: false });
    const [isLoading, setIsLoading] = useState(false);
    const [tokenFieldError, setTokenFieldError] = useState('');
    const [descriptionFieldError, setDescriptionFieldError] = useState('');

    const onClick = async () => {
        if (disabled || isLoading) {
            return;
        }

        const tokenError = validateToken(token);
        if (tokenError) {
            setTokenFieldError(tokenError);

            return;
        }

        if (description.length > maxDescriptionLength) {
            setDescriptionFieldError('Превышен максимальный размер описания');

            return;
        }

        setIsLoading(true);

        try {
            const merchant = await requestMerchantAccess(skill.id, { token, description });

            if (merchants.map((m) => m.entity_id).includes(merchant.entity_id)) {
                dispatchTooltipAction('conflict');

                return;
            }

            afterRequest(merchant);

            dispatchTooltipAction('ok');
        } catch (e) {
            console.error(e);
            dispatchTooltipAction('error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            {...b()}
            onKeyPress={async (event) => {
                if (event.key === 'Enter') {
                    await onClick();
                }
            }}
        >
            <Header caption={requestCaption}>Запрос доступа</Header>
            <Field label="Токен" error={tokenFieldError} required caption={requestTokenCaption}>
                <TextInput
                    autocomplete={false}
                    name="merchantToken"
                    type="text"
                    theme="normal"
                    size="s"
                    text={token}
                    onChange={(value) => {
                        setToken(value);

                        setTokenFieldError(validateToken(value));

                        dispatchTooltipAction('clear');
                    }}
                    onFocus={() => setTokenFieldError('')}
                    disabled={disabled}
                />
            </Field>
            <Field label="Описание" error={descriptionFieldError} caption={requestDescriptionCaption}>
                <TextArea
                    text={description}
                    onChange={(value) => {
                        setDescription(value);

                        setDescriptionFieldError(
                            value.length > maxDescriptionLength ? 'Превышен максимальный размер описания' : '',
                        );

                        dispatchTooltipAction('clear');
                    }}
                    rows={3}
                    limit={maxDescriptionLength}
                    disabled={disabled}
                />
            </Field>
            <TooltipButton
                size="m"
                onOutsideClick={() => dispatchTooltipAction('clear')}
                progress={isLoading}
                onClick={onClick}
                disabled={disabled || Boolean(descriptionFieldError) || Boolean(tokenFieldError)}
                {...tooltipState}
            >
                Отправить
            </TooltipButton>
        </div>
    );
};

export default MerchantRequestForm;
